/**
 * Session courante.
 *
 * Un contexte plutôt qu'un état global : le rôle conditionne l'affichage de
 * dizaines d'éléments, et le faire redescendre en propriétés à travers toute
 * l'arborescence rendrait chaque composant dépendant de son parent.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

import { api, definirSurSessionPerdue } from './api/client'
import { lireRefreshToken, poserAccessToken, poserRefreshToken, purgerJetons } from './api/jetons'
import type { ReponseJetons, RoleUtilisateur, UtilisateurPublic } from './api/types'

interface Session {
  utilisateur: UtilisateurPublic | null
  chargement: boolean
  connecter: (email: string, motDePasse: string) => Promise<UtilisateurPublic>
  deconnecter: () => Promise<void>
  rafraichirProfil: () => Promise<void>
  aLeRole: (...roles: RoleUtilisateur[]) => boolean
  estTitulaire: boolean
  /** Seul lui gere les comptes du cabinet. Les autres titulaires sont ses pairs. */
  estPrincipal: boolean
}

const ContexteSession = createContext<Session | null>(null)

export function FournisseurSession({ children }: { children: ReactNode }) {
  const [utilisateur, setUtilisateur] = useState<UtilisateurPublic | null>(null)
  const [chargement, setChargement] = useState(true)

  const rafraichirProfil = useCallback(async () => {
    try {
      setUtilisateur(await api.get<UtilisateurPublic>('/auth/moi'))
    } catch {
      setUtilisateur(null)
    }
  }, [])

  // Au démarrage : si un refresh token traîne, on tente de reprendre la
  // session. Sinon l'utilisateur serait redéconnecté à chaque rechargement,
  // ce qui rend l'application pénible dès le premier essai.
  useEffect(() => {
    let annule = false
    async function reprendre() {
      if (!lireRefreshToken()) {
        if (!annule) setChargement(false)
        return
      }
      await rafraichirProfil()
      if (!annule) setChargement(false)
    }
    void reprendre()
    return () => {
      annule = true
    }
  }, [rafraichirProfil])

  // Le client HTTP ne connaît pas React : il prévient par ce rappel quand la
  // session devient irrécupérable, et c'est ici qu'on remet l'interface à zéro.
  useEffect(() => {
    definirSurSessionPerdue(() => setUtilisateur(null))
  }, [])

  const connecter = useCallback(async (email: string, motDePasse: string) => {
    const reponse = await api.post<ReponseJetons>('/auth/connexion', {
      email,
      mot_de_passe: motDePasse,
    })
    poserAccessToken(reponse.access_token)
    poserRefreshToken(reponse.refresh_token)
    setUtilisateur(reponse.utilisateur)
    return reponse.utilisateur
  }, [])

  const deconnecter = useCallback(async () => {
    const refresh = lireRefreshToken()
    try {
      if (refresh) await api.post('/auth/deconnexion', { refresh_token: refresh })
    } catch {
      // Une déconnexion qui échoue côté serveur ne doit pas empêcher de sortir.
    }
    purgerJetons()
    setUtilisateur(null)
  }, [])

  const valeur = useMemo<Session>(
    () => ({
      utilisateur,
      chargement,
      connecter,
      deconnecter,
      rafraichirProfil,
      aLeRole: (...roles) => (utilisateur ? roles.includes(utilisateur.role) : false),
      estTitulaire: utilisateur?.role === 'TITULAIRE',
      estPrincipal: utilisateur?.titulaire_principal === true,
    }),
    [utilisateur, chargement, connecter, deconnecter, rafraichirProfil],
  )

  return <ContexteSession.Provider value={valeur}>{children}</ContexteSession.Provider>
}

// Le crochet vit a cote de son fournisseur : les separer imposerait un
// troisieme fichier pour le contexte, sans rien gagner.
// eslint-disable-next-line react-refresh/only-export-components
export function useSession(): Session {
  const contexte = useContext(ContexteSession)
  if (!contexte) throw new Error('useSession doit être utilisé dans un FournisseurSession.')
  return contexte
}
